import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Trash2, X } from "lucide-react";
import { Toaster, toast } from "sonner";
import { LogoMark } from "@/components/site/logo";
import "@/pages/admin.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

async function api(path, options = {}, retry = true) {
  const res = await fetch(`${API}${path}`, { credentials: "include", headers: { "Content-Type": "application/json" }, ...options });
  if (res.status === 401 && retry) {
    const refreshed = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
    if (refreshed.ok) return api(path, options, false);
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(formatApiErrorDetail(data.detail));
  }
  return res.json();
}

const BLANK_FORM = { name: "", location: "", neighborhood: "", type: "Apartment", price: "", price_value: "", beds: 3, area: "", image: "", galleryText: "", status: "Available", description: "" };

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
      onLogin(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-login" data-testid="admin-login-form">
      <LogoMark size={40} className="admin-login-mark" />
      <p className="eyebrow">BRK PRIVATE OFFICE</p>
      <h1>Sign in to<br /><em>the office.</em></h1>
      <form onSubmit={submit}>
        <input type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Admin email" data-testid="admin-email" />
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="Admin password" data-testid="admin-password" />
        {error && <p className="admin-error" data-testid="admin-login-error">{error}</p>}
        <button className="gold-btn" type="submit" disabled={busy} data-testid="admin-login-submit">{busy ? "Signing in…" : "Enter the office"}</button>
      </form>
      <Link to="/" className="admin-back" data-testid="admin-back-home"><ArrowLeft size={14} /> Back to the site</Link>
    </div>
  );
};

const PropertyForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(() => initial
    ? { ...initial, price_value: String(initial.price_value), galleryText: (initial.gallery || []).join("\n") }
    : BLANK_FORM);
  const [busy, setBusy] = useState(false);
  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    const payload = {
      name: form.name, location: form.location, neighborhood: form.neighborhood, type: form.type,
      price: form.price, price_value: Number(form.price_value) || 0, beds: Number(form.beds) || 0,
      area: form.area, image: form.image, status: form.status, description: form.description,
      gallery: form.galleryText.split("\n").map((line) => line.trim()).filter(Boolean),
    };
    try {
      if (initial) await api(`/properties/${initial.id}`, { method: "PUT", body: JSON.stringify(payload) });
      else await api("/properties", { method: "POST", body: JSON.stringify(payload) });
      toast.success(initial ? "Listing updated." : "Listing added to the collection.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="consult-modal admin-form-modal" onClick={(event) => event.stopPropagation()} role="dialog" data-testid="property-form-modal">
        <button className="modal-close" onClick={onClose} aria-label="Close form" data-testid="form-cancel"><X /></button>
        <p className="eyebrow">{initial ? "EDIT LISTING" : "NEW LISTING"}</p>
        <h2>{initial ? <>Refine the<br /></> : <>Add to the<br /></>}<em>collection.</em></h2>
        <form onSubmit={submit} className="admin-form">
          <input required placeholder="Property name" value={form.name} onChange={set("name")} aria-label="Property name" data-testid="form-name" />
          <input required placeholder="Location (e.g. Al Barari · Dubai)" value={form.location} onChange={set("location")} aria-label="Location" data-testid="form-location" />
          <div className="admin-form-row">
            <input placeholder="Neighbourhood" value={form.neighborhood} onChange={set("neighborhood")} aria-label="Neighbourhood" data-testid="form-neighborhood" />
            <select value={form.type} onChange={set("type")} aria-label="Type" data-testid="form-type">
              <option>Apartment</option><option>Villa</option><option>Penthouse</option><option>Townhouse</option>
            </select>
          </div>
          <div className="admin-form-row">
            <input required placeholder="Price label (e.g. AED 8.2M)" value={form.price} onChange={set("price")} aria-label="Price label" data-testid="form-price" />
            <input required type="number" min="0" placeholder="Price in AED (e.g. 8200000)" value={form.price_value} onChange={set("price_value")} aria-label="Price value" data-testid="form-price-value" />
          </div>
          <div className="admin-form-row">
            <input required type="number" min="0" max="20" placeholder="Bedrooms" value={form.beds} onChange={set("beds")} aria-label="Bedrooms" data-testid="form-beds" />
            <input placeholder="Area (e.g. 4,120 sq ft)" value={form.area} onChange={set("area")} aria-label="Area" data-testid="form-area" />
            <select value={form.status} onChange={set("status")} aria-label="Status" data-testid="form-status">
              <option>Available</option><option>Reserved</option><option>Sold</option>
            </select>
          </div>
          <input placeholder="Main image URL" value={form.image} onChange={set("image")} aria-label="Main image URL" data-testid="form-image" />
          <textarea placeholder="Gallery image URLs — one per line" value={form.galleryText} onChange={set("galleryText")} aria-label="Gallery images" data-testid="form-gallery" />
          <textarea placeholder="Description" value={form.description} onChange={set("description")} aria-label="Description" data-testid="form-description" />
          <button className="gold-btn" type="submit" disabled={busy} data-testid="form-submit">{busy ? "Saving…" : initial ? "Save changes" : "Add listing"}</button>
        </form>
      </div>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProperties(await api("/properties"));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (property) => {
    if (!window.confirm(`Remove "${property.name}" from the collection?`)) return;
    try {
      await api(`/properties/${property.id}`, { method: "DELETE" });
      toast.success("Listing removed.");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const logout = async () => {
    try { await api("/auth/logout", { method: "POST" }); } catch { /* session cleared regardless */ }
    onLogout();
  };

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <header className="admin-topbar">
        <div className="admin-topbar-brand"><LogoMark size={28} /><div><strong>BRK Private Office</strong><span>{user.email}</span></div></div>
        <div className="admin-topbar-actions">
          <button className="outline-cta" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-property-button"><Plus size={14} /> Add listing</button>
          <button className="admin-logout" onClick={logout} data-testid="admin-logout">Sign out</button>
        </div>
      </header>
      {loading ? (
        <p className="admin-loading" data-testid="admin-loading">Opening the collection…</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table" data-testid="admin-property-table">
            <thead><tr><th>Listing</th><th>Neighbourhood</th><th>Type</th><th>Price</th><th>Beds</th><th>Status</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {properties.map((property) => (
                <tr key={property.id} data-testid={`property-row-${property.id}`}>
                  <td><div className="admin-listing"><img src={property.image} alt="" /><div><strong>{property.name}</strong><span>{property.location}</span></div></div></td>
                  <td>{property.neighborhood || "—"}</td>
                  <td>{property.type}</td>
                  <td>{property.price}</td>
                  <td>{property.beds}</td>
                  <td><span className={`admin-status status-${property.status.toLowerCase()}`}>{property.status}</span></td>
                  <td className="admin-actions">
                    <button onClick={() => { setEditing(property); setFormOpen(true); }} aria-label={`Edit ${property.name}`} data-testid={`edit-property-${property.id}`}><Pencil size={14} /></button>
                    <button onClick={() => remove(property)} aria-label={`Delete ${property.name}`} data-testid={`delete-property-${property.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {properties.length === 0 && <tr><td colSpan="7" className="admin-empty" data-testid="admin-empty">No listings yet — add your first one.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {formOpen && <PropertyForm initial={editing} onClose={() => setFormOpen(false)} onSaved={load} />}
    </div>
  );
};

function AdminPage() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api("/auth/me").then(setUser).catch(() => setUser(null));
  }, []);

  return (
    <div className="admin-shell">
      <Toaster position="bottom-right" toastOptions={{ style: { background: "#f6f1e9", color: "#151918", border: "1px solid #d4af37" } }} />
      {user === undefined ? (
        <p className="admin-loading" data-testid="admin-checking">Checking your session…</p>
      ) : user ? (
        <Dashboard user={user} onLogout={() => setUser(null)} />
      ) : (
        <LoginForm onLogin={setUser} />
      )}
    </div>
  );
}

export default AdminPage;
